import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { GlobalVariable } from '../helpers/settings';
import { Result } from '../models/general';
import { InvoiceModel } from '../models/invoice-model';
import { RefreshVerificationService } from './refresh-verification.service';

export interface PaginationData {
	docs: InvoiceModel[];
	total: number;
	limit: number;
	page: number;
	pages: number;
}

@Injectable({
	providedIn: 'root'
})
export class InvoiceModelService {
	readonly BASE_URL = `${GlobalVariable.server.host}/invoice-model`;

	constructor(private httpClient: HttpClient, private refreshVerifService: RefreshVerificationService) {}

	find(
		fromAppComponent: boolean,
		route: string,
		projectId: string,
		attribute: string = null
	): Observable<InvoiceModel[]> {
		let params;
		if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId);
		} else {
			params = new HttpParams().set('fromAppComponent', fromAppComponent);
		}
		if (attribute) {
			params = params.set('attribute', attribute);
		}
		if (projectId) {
			params = params.set('project', projectId);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.get<InvoiceModel[]>(`${this.BASE_URL}/find`, { params })
		);
	}

	findByFilter(data: {
		fromAppComponent?: boolean;
		route?: string;
		projectId?: string;
		filter?: any;
		attributes?: string;
	}): Observable<InvoiceModel[]> {
		const { fromAppComponent, route, projectId, filter, attributes } = data ?? {};

		let params = new HttpParams();
		if (attributes) {
			params = params.set('attributes', attributes);
		}
		if (filter) {
			params = params.set('filter', JSON.stringify(filter ?? {}));
		}
		if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId);
		} else {
			params = params.set('fromAppComponent', fromAppComponent);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.get<InvoiceModel[]>(`${this.BASE_URL}/find-by-filter`, { params })
		);
	}
	findByProject(
		projectId: string,
		fromAppComponent: boolean,
		route: string,
		attributes: string = null,
		filter?: any
	): Observable<InvoiceModel[]> {
		let params;
		if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId);
		} else {
			params = new HttpParams().set('fromAppComponent', fromAppComponent);
		}
		// attributes: string, filter
		if (attributes) params = params.set('attributes', attributes);
		if (filter) params = params.set('filter', JSON.stringify(filter ?? {}));
		return this.refreshVerifService.buidResult(
			this.httpClient.get<InvoiceModel[]>(`${this.BASE_URL}/find-by-project/${projectId}`, { params })
		);
	}

	findByShippingModels(
		shippingModelsId: string[],
		projectId?: string,
		fromAppComponent?: boolean,
		route?: string
	): Observable<InvoiceModel[]> {
		let params;
		if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId);
		} else {
			params = new HttpParams().set('fromAppComponent', fromAppComponent);
		}
		params = params.set('shippingModelsId', JSON.stringify(shippingModelsId) || '');
		return this.refreshVerifService.buidResult(
			this.httpClient.get<InvoiceModel[]>(`${this.BASE_URL}/find-by-shipping-models`, {
				params
			})
		);
	}

	findById(id: string, fromAppComponent: boolean, route: string, projectId: string): Observable<InvoiceModel> {
		let params;
		if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId);
		} else {
			params = new HttpParams().set('fromAppComponent', fromAppComponent);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.get<InvoiceModel>(`${this.BASE_URL}/find-by-id/${id}`, { params })
		);
	}

	create(invoiceModel: Partial<InvoiceModel>): Observable<{ status: number; message: string; result: any }> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{ status: number; message: string; result: any }>(
				`${this.BASE_URL}/create`,
				invoiceModel
			)
		);
	}

	setAsDefualt(model: Partial<InvoiceModel>): Observable<{ _id?: string }> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{ _id: string }>(`${this.BASE_URL}/set-as-default`, model)
		);
	}

	duplicate(model: Partial<InvoiceModel>): Observable<{ _id?: string }> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{ _id: string }>(`${this.BASE_URL}/duplicate`, model)
		);
	}

	edit(invoiceModel: Partial<InvoiceModel>): Observable<Result> {
		return this.refreshVerifService.buidResult(this.httpClient.put<Result>(`${this.BASE_URL}/edit`, invoiceModel));
	}

	delete(invoiceModelId: string): Observable<Result> {
		return this.refreshVerifService.buidResult(
			this.httpClient.delete<Result>(`${this.BASE_URL}/delete/${invoiceModelId}`)
		);
	}

	uploadInvoiceModelLogo(file?: any, id?: string, actionName?: string): Observable<InvoiceModel> {
		let params = new HttpParams().set('actionName', actionName);
		const formData: FormData = new FormData();
		if (!file) return of(null);
		formData.append('image', file, file.name);
		return this.refreshVerifService.buidResult(
			this.httpClient.post<InvoiceModel>(`${this.BASE_URL}/upload-image/${id}`, formData, { params })
		);
	}

	archive(invoiceModelId: string): Observable<Result> {
		return this.refreshVerifService.buidResult(
			this.httpClient.delete<Result>(`${this.BASE_URL}/archive/${invoiceModelId}`)
		);
	}

	isUsed(invoiceModelId: string): Observable<Result> {
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Result>(`${this.BASE_URL}/if-is-Used/${invoiceModelId}`)
		);
	}
}
